import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { SearchParam, TableType } from '@common/ts';
import { ChevronDown, Check, PlusSquare, Trash2, Pencil, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import EditFilterModal from '@/components/Modals/FilterModal/EditFilterModal';
import { useMutateDeleteFilter } from '@/reactQuery/post';
import { useGetFilters } from '@/reactQuery/get';
import DeleteFilterModal from '@/components/Modals/FilterModal/DeleteFilterModal';
import { FiltersSelectProps } from '@/types';
import { getPaginationParamPage } from '@/utils/urlParams';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import TooltipWrapper from '@/components/tooltip/TooltipWrapper';

function FiltersSelect({
  onReRun,
  rerunFilterValues,
  refetch,
  onFilterDeleteEnableRefetch,
  onCompare,
}: FiltersSelectProps) {
  const { project_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const isEdit = searchParams.get('edit') === 'true';
  const [selectedFilter, setSelectedFilter] = useState(filterId || '');
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [filterIdToDelete, setFilterIdToDelete] = useState<string | null>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);

  const { data: filtersListData } = useGetFilters(project_id);
  // Derive from filtersListData so the modal hydrates with the existing
  // filter's name + customFields. A previous Popover rewrite (commit
  // 66b1563ac) accidentally replaced this with `useState<string|null>`
  // that was never set, leaving Edit filter modals empty.
  const editFilterData = filtersListData?.find((item: { _id: string }) => item?._id === filterId);

  const addQueryParams = (params: Record<string, string>) => {
    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    setSearchParams(searchParams);
  };

  const removeSelectedFilter = () => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.delete(SearchParam.FILTER_ID);
    newSearchParams.delete('edit');
    setSearchParams(newSearchParams);
  };

  useEffect(() => {
    if (isEdit) {
      setOpenEditModal(true);
      const newSearchParams = new URLSearchParams(searchParams.toString());
      newSearchParams.delete('edit');
      setSearchParams(newSearchParams);
    }
  }, []);

  const {
    mutateAsync: mutateAsyncDeleteFilter,
    isPending: isFilterDeletionPending,
    isSuccess,
  } = useMutateDeleteFilter();

  const handleDeleteFilter = async (f_id: string | null) => {
    try {
      onFilterDeleteEnableRefetch(false);
      await mutateAsyncDeleteFilter({
        project_id,
        [SearchParam.FILTER_ID]: f_id,
      });

      if (f_id === filterId) {
        removeSelectedFilter();
        setSelectedFilter('');
      }
      setIsDeleteModalOpen(false);
      toast.success('Filter deleted successfully');
    } catch {
      toast.error('Failed to delete filter');
    }
  };

  const handleEditFilter = (id: string) => {
    addQueryParams({ [SearchParam.FILTER_ID]: id, edit: 'true' });
    setOpenEditModal(true);
  };

  const handleSettingFilter = (id: string) => {
    if (id) {
      const newSearchParams = new URLSearchParams(searchParams.toString());
      const paramsToRemove = [
        getPaginationParamPage(TableType.TRANSCRIPTS),
        getPaginationParamPage(TableType.INCOMPLETE_TRANSCRIPTS),
        getPaginationParamPage(TableType.SCREENED_OUT_TRANSCRIPTS),
        getPaginationParamPage(TableType.TRANSLATED_TRANSCRIPTS),
      ];
      paramsToRemove.forEach((param) => newSearchParams.delete(param));
      newSearchParams.set(SearchParam.FILTER_ID, id);
      setSearchParams(newSearchParams);
    } else {
      removeSelectedFilter();
    }
    setSelectedFilter(id);
    setPopoverOpen(false);
  };

  const getFilterNameById = (id: string | null) => {
    if (filtersListData && id) {
      const filter = filtersListData?.find((f: { _id: string }) => f._id === id);
      return filter ? filter.name : '';
    }
    return '';
  };

  const activeFilter = !!selectedFilter && !!getFilterNameById(selectedFilter);
  const isFilterProcessing = rerunFilterValues.includes(selectedFilter);

  useEffect(() => {
    if (filterId) {
      setSelectedFilter(filterId);
    }
  }, [filterId]);

  useEffect(() => {
    if (isSuccess) {
      onFilterDeleteEnableRefetch(true);
    }
  }, [isSuccess]);

  return (
    <>
      <div className="flex items-center gap-2">
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="flex relative truncate cursor-pointer w-52 h-9 items-center justify-between rounded-md border border-neutral-200 font-medium px-3 py-1.5 text-sm hover:bg-neutral-50 transition-colors"
            >
              <span className="flex items-center gap-2 min-w-0">
                {activeFilter && (
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${isFilterProcessing ? 'bg-amber-400 animate-pulse' : 'bg-emerald-500'}`}
                  />
                )}
                <span className="truncate text-neutral-700">
                  {getFilterNameById(selectedFilter) || 'Select filter'}
                </span>
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 text-neutral-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-64 p-0"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="max-h-[300px] overflow-y-auto py-1">
              {/* Deselect option */}
              <button
                type="button"
                className={`flex items-center gap-2 w-full px-3 py-2 text-sm hover:bg-neutral-100 transition-colors ${
                  !selectedFilter ? 'text-emerald-700 bg-emerald-50' : 'text-neutral-700'
                }`}
                onClick={() => handleSettingFilter('')}
              >
                {!selectedFilter ? (
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                ) : (
                  <span className="h-4 w-4 shrink-0" />
                )}
                Deselect filters
              </button>

              {filtersListData && filtersListData.length > 0 && (
                <div className="border-t border-neutral-100 my-1" />
              )}

              {/* Filter items */}
              {filtersListData?.map((filter: { _id: string; name: string }) => {
                const isDisabled = rerunFilterValues.includes(String(filter._id));
                const isSelected = selectedFilter === String(filter._id);

                const item = (
                  <div
                    key={filter._id}
                    className={`flex items-center gap-2 w-full px-3 py-2 text-sm transition-colors ${
                      isDisabled
                        ? 'opacity-50 cursor-not-allowed'
                        : 'hover:bg-neutral-100 cursor-pointer'
                    } ${isSelected ? 'bg-emerald-50 text-emerald-700' : 'text-neutral-700'}`}
                  >
                    <button
                      type="button"
                      className="flex items-center gap-2 flex-1 min-w-0"
                      disabled={isDisabled}
                      onClick={() => !isDisabled && handleSettingFilter(String(filter._id))}
                    >
                      {isSelected ? (
                        <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                      ) : (
                        <span className="h-4 w-4 shrink-0" />
                      )}
                      <span className="truncate">{filter.name}</span>
                    </button>
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPopoverOpen(false);
                          handleEditFilter(String(filter._id));
                        }}
                        className={`p-1 rounded hover:text-emerald-700 hover:bg-neutral-200 transition-colors ${
                          isDisabled ? 'pointer-events-none' : ''
                        }`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPopoverOpen(false);
                          setFilterIdToDelete(String(filter._id));
                          setIsDeleteModalOpen(true);
                        }}
                        className={`p-1 rounded hover:text-red-600 hover:bg-neutral-200 transition-colors ${
                          isDisabled ? 'pointer-events-none' : ''
                        }`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );

                if (isDisabled) {
                  return (
                    <TooltipWrapper key={filter._id} text="This filter is being processed">
                      {item}
                    </TooltipWrapper>
                  );
                }

                return item;
              })}
            </div>

            {/* Add filter button */}
            <div className="border-t border-neutral-100 p-1.5">
              <Button
                variant="default"
                size="sm"
                className="w-full"
                onClick={() => {
                  setPopoverOpen(false);
                  setOpenEditModal(true);
                }}
                disabled={isFilterDeletionPending}
              >
                {isFilterDeletionPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <PlusSquare className="h-4 w-4 mr-2" />
                    Add Filter
                  </>
                )}
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {onCompare && filtersListData && filtersListData.length >= 2 && (
          <Button variant="outline" size="sm" onClick={onCompare}>
            Compare
          </Button>
        )}
      </div>

      {openEditModal && (
        <EditFilterModal
          project_id={project_id}
          setOpenEditModal={setOpenEditModal}
          onReRun={onReRun}
          editFilterData={editFilterData}
          refetch={refetch}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteFilterModal
          isOpen={isDeleteModalOpen}
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={() => handleDeleteFilter(filterIdToDelete)}
        />
      )}
    </>
  );
}

export default FiltersSelect;
